import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Upload, CheckCircle2, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";

export const ImportPastAuditDialog = ({ open, onOpenChange, onImported }) => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ hotel_id: "", audit_date: "", auditor_name: "", template_id: "new" });
  const fileRef = useRef();
  const hotelsRef = useRef([]);

  useEffect(() => {
    if (open) {
      api.get("/hotels").then((r) => { setHotels(r.data); hotelsRef.current = r.data; });
      api.get("/templates").then((r) => setTemplates(r.data));
      setPreview(null);
      setForm({ hotel_id: "", audit_date: "", auditor_name: "", template_id: "new" });
    }
  }, [open]);

  const matchHotel = (fileName, hotelList) => {
    const fn = fileName.toLowerCase();
    let best = null, bestScore = 0;
    for (const h of hotelList) {
      const words = h.name.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3 && !["hotel", "resort"].includes(w));
      let score = words.filter((w) => fn.includes(w)).length;
      if (h.city && fn.includes(h.city.toLowerCase())) score += 2;
      if (score > bestScore) { bestScore = score; best = h; }
    }
    return bestScore >= 2 ? best : null;
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/templates/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const detected = matchHotel(file.name, hotelsRef.current);
      if (detected) {
        setForm((f) => ({ ...f, hotel_id: detected.id }));
        toast.info(`Hotel auto-detected from file name: ${detected.name}`);
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setUploading(false);
    }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/audits/import-historical", {
        name: preview.name,
        description: preview.description,
        sections: preview.sections,
        hotel_id: form.hotel_id,
        audit_date: form.audit_date,
        auditor_name: form.auditor_name || "Historical Import",
        template_id: form.template_id === "new" ? null : form.template_id,
      });
      toast.success(`Past audit imported — score ${data.scores.overall_score}%${data.skipped ? ` (${data.skipped} unmatched questions skipped)` : ""}`);
      onOpenChange(false);
      onImported?.();
      navigate(`/audits/${data.audit_id}/report`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#14532D]" />Import Past Audit</DialogTitle>
          <DialogDescription>Upload an old audit Excel with points earned — it becomes a completed audit with real scores in your trends.</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div
            className="border-2 border-dashed border-stone-300 rounded-xl p-10 text-center cursor-pointer hover:border-[#14532D] hover:bg-emerald-50/30 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="past-audit-dropzone"
          >
            <Upload className="w-10 h-10 mx-auto text-stone-400" strokeWidth={1.2} />
            <p className="mt-3 text-sm font-medium text-stone-700">{uploading ? "Reading audit results..." : "Click to choose the completed audit .xlsx"}</p>
            <p className="text-xs text-stone-400 mt-1">Points value, points earned and auditor comments are detected automatically.</p>
            <input ref={fileRef} data-testid="past-audit-file-input" type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => uploadFile(e.target.files[0])} />
            {uploading && <div className="mt-4 w-6 h-6 mx-auto border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" />}
          </div>
        ) : (
          <div className="space-y-4" data-testid="past-audit-preview">
            {preview.has_results ? (
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Detected {preview.stats.sections} sections, {preview.stats.questions} questions with past results in "{preview.name}"
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                No "points earned" values found — this file has the checklist but no results, so all answers will import as N/A. You can still proceed or choose another file.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Hotel audited <span className="text-rose-500">*</span></Label>
                <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v })}>
                  <SelectTrigger data-testid="past-audit-hotel-select"><SelectValue placeholder="Assign this audit to a hotel" /></SelectTrigger>
                  <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-stone-400">The imported audit and its scores will be assigned to this hotel. Auto-selected when the file name mentions the hotel.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Audit date</Label>
                <Input data-testid="past-audit-date-input" type="date" value={form.audit_date} onChange={(e) => setForm({ ...form, audit_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Auditor name</Label>
                <Input data-testid="past-audit-auditor-input" placeholder="e.g. Ms. Divya Gaikwad" value={form.auditor_name} onChange={(e) => setForm({ ...form, auditor_name: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Template</Label>
                <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                  <SelectTrigger data-testid="past-audit-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Create new template from this file</SelectItem>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>Attach to: {t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-stone-400">Attaching to an existing template keeps all past audits of the same checklist comparable (questions matched by text).</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setPreview(null)}>Choose another file</Button>
              <Button data-testid="past-audit-import-button" className="flex-1 bg-[#14532D] hover:bg-[#166534]" disabled={importing || !form.hotel_id || !form.audit_date} onClick={doImport}>
                {importing ? "Importing..." : "Import Past Audit"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
